import "./Live.css";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { type Column, SidePanel, Table } from "../../kit/index.js";
import { useTitle } from "../context.js";
import { errorMessage, invalidate, readTraining, revalidate, trainingKey, useResource } from "../data.js";
import { todayString, weekdayLong } from "../model/days.js";
import { agoText, type Board, type BoardGroup, type BoardRow, type FeedItem, fastestText, rowAgoText, startedText, trainingBoard, trainingNow } from "../model/training.js";
import { Icon } from "../parts.js";
import { usePrefs } from "../prefs.js";
import type { TrainingDay } from "../model/types.js";
import { WorkoutSets } from "./Athlete.js";

/** DESIGN §41.5: the page reads itself again this often while shown, so "17 min" and Quiet move without a notice. */
export const LIVE_REFRESH_MS = 30_000;

export const LiveIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="2.2" fill="currentColor" />
    <path d="M6.6 6.6a4.8 4.8 0 0 0 0 6.8M13.4 6.6a4.8 4.8 0 0 1 0 6.8M4.2 4.2a8.2 8.2 0 0 0 0 11.6M15.8 4.2a8.2 8.2 0 0 1 0 11.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/** How many are training today (DESIGN §41.5): read on load and again on every `training` notice. */
export function useTrainingNow(): number {
  const date = todayString();
  const day = useResource(trainingKey(date), () => readTraining(date));
  return day.value ? trainingNow(trainingBoard(day.value, new Date())) : 0;
}

/** The menu's Live, with `● n` while the board is live. */
export function LiveNavItem({ current }: { current: boolean }) {
  const n = useTrainingNow();
  return (
    <Link className="nav-item" to="/live" title={n ? `Live · ${n} training now` : "Live"} aria-current={current ? "page" : undefined}>
      {LiveIcon}
      <span className="nav-label">
        Live
        {n ? (
          <span className="lv-nav-count">
            <span aria-hidden="true"> ● {n}</span>
            <span className="sr">, {n} training now</span>
          </span>
        ) : null}
      </span>
    </Link>
  );
}

const athletePath = (r: BoardRow) => "/athlete/" + encodeURIComponent(r.athleteID);

function State({ row }: { row: BoardRow }) {
  return (
    <span className={"lv-state is-" + row.state}>
      <span className="lv-dot" aria-hidden="true" />
      {row.word}
    </span>
  );
}

function Record({ label }: { label: string }) {
  return (
    <span className="lv-record">
      {Icon.trophy}
      <span>{label}</span>
    </span>
  );
}

function Squares({ row }: { row: BoardRow }) {
  const p = row.progress;
  if (!p) return <span className="k-muted">—</span>;
  return (
    <div className="lv-progress">
      {p.total > 0 ? (
        <span className="lv-squares" role="img" aria-label={`${p.done} of ${p.total} sets` + (p.skipped ? `, ${p.skipped} skipped` : "")}>
          {p.blocks.map((b) => (
            <span className="lv-block" key={b.letter}>
              <span className="lv-letter" aria-hidden="true">
                {b.letter}
              </span>
              {b.slots.map((s) => (
                <span key={s.exerciseID + ":" + s.setIndex} className={"lv-sq" + (s.done ? " is-done" : "") + (s.skipped ? " is-skipped" : "") + (s.next ? " is-next" : "")} title={s.skipped ? "skipped" : undefined} />
              ))}
            </span>
          ))}
        </span>
      ) : null}
      {p.notLogged.map((label) => (
        <span className="k-sub" key={label}>
          {label} · not logged
        </span>
      ))}
    </div>
  );
}

function Group({ group, now, index, selected }: { group: BoardGroup; now: Date; index: number; selected: string | null }) {
  const hid = "lv-group-" + index;
  const columns: Column<BoardRow>[] = [
    {
      key: "athlete",
      label: "Athlete",
      render: (r) => (
        <>
          <span className="lv-name">{r.name}</span>
          <span className="lv-sub">
            <State row={r} />
            {r.detail && r.detail !== r.word ? <span className="lv-detail">{r.detail}</span> : null}
          </span>
        </>
      ),
    },
    { key: "progress", label: "Progress", wrap: true, render: (r) => <Squares row={r} /> },
    {
      key: "last",
      label: "Last set",
      wrap: true,
      render: (r) =>
        r.last ? (
          <span className="lv-last">
            <span className="num">{r.last.text}</span>
            {r.last.record ? <Record label={r.last.record} /> : null}
            {r.at ? <span className="k-phone-only k-sub num">{rowAgoText(r.at, now)}</span> : null}
          </span>
        ) : (
          <span className="k-muted">—</span>
        ),
    },
    { key: "logged", label: "Logged", align: "end", className: "k-hide-phone", render: (r) => (r.at ? <span className="num">{rowAgoText(r.at, now)}</span> : <span className="k-muted">—</span>) },
  ];
  return (
    <section className="lv-group" aria-labelledby={hid}>
      <div className="lv-group-head">
        <h2 id={hid}>{group.title}</h2>
        {group.fastest.map((f) => (
          <p className="lv-fastest num" key={f.meters}>
            {fastestText(f)}
          </p>
        ))}
      </div>
      <Table rows={group.rows} columns={columns} rowKey={(r) => r.athleteID} href={workoutPath} selected={selected} label={group.title} empty="Nobody in this group" />
    </section>
  );
}

function Feed({ items, now }: { items: FeedItem[]; now: Date }) {
  return (
    <section className="lv-feed" aria-labelledby="lv-feed-h">
      <h2 id="lv-feed-h">As it happens</h2>
      {items.length ? (
        <ol className="lv-feed-list">
          {items.map((f) => (
            <li key={f.key}>
              <time className="lv-when num" dateTime={f.at.toISOString()}>
                {agoText(f.at, now)}
              </time>
              <span className="lv-what">
                <Link className="lv-who" to={"/athlete/" + encodeURIComponent(f.athleteID)}>
                  {f.name}
                </Link>{" "}
                {f.what}
                {f.value ? <span className="lv-value num">{f.value}</span> : null}
                {f.record ? <Record label={f.record} /> : null}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="k-empty">Nothing logged yet.</p>
      )}
    </section>
  );
}

function Counts({ board }: { board: Board }) {
  const tiles: [string, number][] = [
    ["Training", board.counts.training],
    ["Quiet", board.counts.quiet],
    ["Done", board.counts.done],
    ["Not started", board.counts.notStarted],
    ["Bests today", board.bests],
  ];
  if (board.counts.skipped) tiles.splice(3, 0, ["Skipped", board.counts.skipped]);
  return (
    <dl className="lv-counts">
      {tiles.map(([label, n]) => (
        <div className="lv-tile" key={label}>
          <dt>{label}</dt>
          <dd className="num">{n}</dd>
        </div>
      ))}
    </dl>
  );
}

/** DESIGN §41.5: today's team workout as it happens, a card per group and the sets as they land. */
export function Live() {
  usePrefs();
  useTitle("Live");
  const [, tick] = useState(0);
  const date = todayString();
  const key = trainingKey(date);
  const day = useResource(key, () => readTraining(date));

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      tick((n) => n + 1);
      revalidate(trainingKey(todayString()));
    }, LIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const { athleteID } = useParams();
  const now = new Date();
  const board = day.value ? trainingBoard(day.value, now) : null;
  const open = board && athleteID ? board.groups.flatMap((g) => g.rows).find((r) => r.athleteID === athleteID) : undefined;
  const started = board ? startedText(board) : null;

  return (
    <div className="k-page lv-page">
      <header className="k-header">
        <div className="k-header-title">
          <div className="lv-title">
            <h1>{weekdayLong(date)}</h1>
            {board?.live ? (
              <span className="lv-pill">
                <span className="lv-pill-dot" aria-hidden="true" />
                Live
              </span>
            ) : board ? (
              <span className="lv-none">Nobody training now</span>
            ) : null}
          </div>
          {started ? <span className="k-header-context">{started}</span> : null}
        </div>
      </header>
      {!board ? (
        day.error ? (
          <div className="k-empty" role="alert">
            <p>{errorMessage(day.error)}</p>
            <button type="button" className="k-btn" onClick={() => invalidate(key)}>
              Try again
            </button>
          </div>
        ) : (
          <p className="k-empty" role="status">
            Loading…
          </p>
        )
      ) : board.empty ? (
        <p className="k-empty">Nothing on today's plan.</p>
      ) : (
        <>
          <Counts board={board} />
          <div className="lv-layout">
            <div className="lv-cards">
              {board.groups.map((g, i) => (
                <Group group={g} now={now} index={i} selected={athleteID ?? null} key={g.group ?? "other"} />
              ))}
            </div>
            <Feed items={board.feed} now={now} />
          </div>
          {open && day.value ? <WorkoutPanel row={open} day={day.value} date={date} /> : null}
        </>
      )}
    </div>
  );
}

const workoutPath = (r: BoardRow) => "/live/" + encodeURIComponent(r.athleteID);

/** The entry the board read the athlete from: a finished workout wins, else the one under way most lately. */
function entryOf(day: TrainingDay, athleteID: string) {
  const mine = (list: TrainingDay["finished"]) => (list ?? []).filter((e) => e.athleteID === athleteID);
  const last = (list: TrainingDay["finished"]) => mine(list).sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))[0];
  return last(day.finished) ?? last(day.training);
}

/** One athlete's workout of the day, set by set, beside the board (DESIGN §41.5). */
function WorkoutPanel({ row, day, date }: { row: BoardRow; day: TrainingDay; date: string }) {
  const entry = entryOf(day, row.athleteID);
  const subtitle = [row.word, row.detail && row.detail !== row.word ? row.detail : ""].filter(Boolean).join(" · ");
  return (
    <SidePanel
      title={row.name}
      subtitle={subtitle}
      closeTo="/live"
      actions={
        <Link className="k-btn" to={athletePath(row)}>
          Athlete page
        </Link>
      }
    >
      {entry?.session ? (
        <>
          {row.progress?.notLogged.length ? (
            <p className="k-muted">{row.progress.notLogged.map((label) => label + " · not logged").join(" · ")}</p>
          ) : null}
          <WorkoutSets w={{ date, title: entry.session.title ?? "", session: entry.session, records: entry.records ?? [] }} />
        </>
      ) : (
        <p className="k-muted">Nothing logged yet today.</p>
      )}
    </SidePanel>
  );
}
